import { Button, Collapse } from 'antd';
import EventJourney from 'emp-3.0/components/Funnel/EventJourney';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import Icon, { CaretRightOutlined } from '@ant-design/icons';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import PlusIcon from '@icons/emp/plus_icon.svg';
import { useGetSankeyDimensions } from '@src/emp-3.0/api/queryHooks';
import { useFunnelStore } from '@src/emp-3.0/components/Funnel/store';
import { FilterList } from '@src/emp-3.0/components/Sankey/GlobalFilter';
import { IEventFilter } from '@src/emp-3.0/types';
import { parseDataToOptions } from '@src/emp-3.0/utils/parse';

import { CollapseHeader, StyledCollapse, Title } from './styled';

const EMPTY_ARRAY = [];
const FUNNEL_STEPS_MAX = 10;
export const FUNNEL_STEPS_MIN = 1;
const FUNNEL_STEPS_WITH_MAX_DIMENSION_RULES_COUNT = 10;
const EVENT_NAME = 'event_name';
export const eventJourneyOptions = [
  {
    label: 'Starting with',
    value: 'forward',
  },
  {
    label: 'Ending with',
    value: 'backward',
  },
];

export const RenderEventJourney = ({
  funnel_events,
  interval,
  filters,
  isEventNameLoading,
  eventNameOptions,
  eventDimensionOptions,
  emitValues,
}: {
  funnel_events: any;
  isEventNameLoading: boolean;
  eventNameOptions: any;
  eventDimensionOptions: any;
  interval?: string;
  filters?: IEventFilter[][];
  emitValues?: any;
}) => {
  const [landingEvents, setLandingEvents] = useState(funnel_events);

  useEffect(() => {
    setLandingEvents(funnel_events);
  }, [funnel_events]);

  const addDimension = (index) => {
    const cloneLandingEvents = cloneDeep(landingEvents);
    cloneLandingEvents[index].filters.push([
      {
        field: '',
        op: 'eq',
        value: [],
      },
    ]);
    setLandingEvents(cloneLandingEvents);
  };
  const eventsChange = (index, eventData) => {
    const cloneLandingEvents = cloneDeep(landingEvents);
    cloneLandingEvents[index] = eventData;
    setLandingEvents(cloneLandingEvents);
    emitValues(cloneLandingEvents);
  };
  // max 6 steps, min 1 steps
  const EventJourneyContent = (
    <div style={{ paddingLeft: 24 }}>
      {landingEvents.map((event, index) => {
        const showAddDimensionButton =
          Array.isArray(event?.filters) &&
          event.filters.length < FUNNEL_STEPS_WITH_MAX_DIMENSION_RULES_COUNT;

        return (
          <EventJourney
            style={{ marginBottom: 8, width: '100%' }}
            compactTitle={`Step ${index + 1}: `}
            key={`${event.event_name}_${index}`}
            loading={isEventNameLoading}
            interval={interval}
            filters={filters}
            showDelete={landingEvents.length > FUNNEL_STEPS_MIN}
            addDimensionButtonProps={{
              show: showAddDimensionButton,
              text: '',
            }}
            showIconStyle={true}
            minWidth="250px"
            eventData={event}
            eventOptions={eventNameOptions}
            onAddDimension={() => {
              addDimension(index);
            }}
            onChange={(...rest) => eventsChange(index, ...rest)}
            compactDualSelectProps={{
              dimensionSelectProps: {
                options: eventDimensionOptions,
              },
              dimensionValueSelectProps: {
                mode: undefined,
                style: { flex: 1 },
              },
            }}
            onDeleteEvent={() => {
              if (landingEvents.length > FUNNEL_STEPS_MIN) {
                const cloneLandingEvents = cloneDeep(landingEvents);
                cloneLandingEvents.splice(index, 1);
                setLandingEvents(cloneLandingEvents);
                emitValues(cloneLandingEvents);
              }
            }}
          />
        );
      })}
    </div>
  );

  return (
    <Collapse.Panel
      key={1}
      header={<CollapseHeader>Pick events to define funnel steps</CollapseHeader>}
    >
      {EventJourneyContent}
      {landingEvents.length < FUNNEL_STEPS_MAX && (
        <Button
          disabled={landingEvents.length >= FUNNEL_STEPS_MAX}
          type="link"
          onClick={() => {
            setLandingEvents([
              ...landingEvents,
              {
                event_name: '',
                filters: [],
              },
            ]);
          }}
          style={{ width: 154, paddingInlineStart: 0 }}
        >
          <Icon component={PlusIcon} />
          Add Step ({FUNNEL_STEPS_MAX - landingEvents.length})
        </Button>
      )}
    </Collapse.Panel>
  );
};

const Configurations = ({ onFetch, loading, invalidParams, disableStyle }) => {
  const { funnelRequestBody, setFunnelRequestBody } = useFunnelStore((state) => state);
  const { filters, funnel_events, interval, dimension_breakdowns } = funnelRequestBody;
  const { data: sankeyDimensionList = EMPTY_ARRAY } = useGetSankeyDimensions();
  const { data: eventNameData = EMPTY_ARRAY, isFetching: isEventNameLoading } =
    useQueryDimensionData(
      {
        fields: [EVENT_NAME],
        interval: interval,
        filters: filters,
        params: {
          src: 'trends',
        },
        options: {
          enabled: !!interval,
        },
      },
      500,
    );
  const eventDimensionOptions = parseDataToOptions(
    sankeyDimensionList.filter((item) => item.can_breakdown === 1),
    {
      label: 'display_name',
      value: 'dimension_name',
    },
  );
  const breakDownDimensionOptions =
    parseDataToOptions(
      sankeyDimensionList.filter((item) => item.can_funnel_breakdown === 1),
      {
        label: 'display_name',
        value: 'dimension_name',
      },
    ) || [];
  const landingEventNameOptions = parseDataToOptions(eventNameData, {
    label: EVENT_NAME,
    value: EVENT_NAME,
  });

  const disableRefresh = useMemo(() => {
    if (loading) {
      return false;
    }
    if (isEventNameLoading) {
      return true;
    }
    if (invalidParams) {
      return true;
    }
    return false;
  }, [isEventNameLoading, invalidParams, loading]);

  const mergeFilters = (target, from) => {
    // assign filters from from to target
    const newFilters = cloneDeep(target);
    from.forEach((filter) => {
      const index = newFilters.findIndex((f) => f[0].field === filter.field);
      if (index > -1) {
        newFilters[index][0].value = filter.value.concat(newFilters[index][0].value);
      } else {
        newFilters.push(filter);
      }
    });
    return newFilters;
  };

  const breakdownFilters = mergeFilters(filters, funnel_events[0]?.filters || []);

  return (
    <div style={{ width: 450, borderRight: '1px solid rgba(0, 0, 0, 0.15)', ...disableStyle }}>
      <Title>
        <span style={{ padding: '4px 8px' }}>Configurations</span>
        <Button
          type="primary"
          onClick={() => {
            onFetch();
          }}
          loading={loading}
          disabled={disableRefresh}
        >
          Refresh Funnel
          <CaretRightOutlined />
        </Button>
      </Title>
      <StyledCollapse defaultActiveKey={['1', '2']} ghost>
        {RenderEventJourney({
          interval,
          filters,
          funnel_events,
          isEventNameLoading,
          eventNameOptions: landingEventNameOptions,
          eventDimensionOptions,
          emitValues: (value) => {
            setFunnelRequestBody({
              funnel_events: value,
            });
          },
        })}

        <Collapse.Panel key={2} header={<CollapseHeader>Breakdown Event</CollapseHeader>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 24 }}>
            <FilterList
              interval={interval}
              dimensionOptions={breakDownDimensionOptions}
              fixedDimensions={[]}
              filters={dimension_breakdowns}
              onChange={(value) => {
                setFunnelRequestBody({ dimension_breakdowns: value });
              }}
              showOneFilter={true}
              style={{ flexDirection: 'column' }}
              globalFilters={breakdownFilters}
            />
          </div>
        </Collapse.Panel>
      </StyledCollapse>
    </div>
  );
};

export default Configurations;
